<script setup>
  import ComponentAlert from "@components/Alerts/Alert.vue";
  import ComponentIcon from "@components/Icons/Icon.vue";
  import MergeAccountIconGroup from "@pages/MergeAccount/Components/IconGroup.vue";
  import MergeAccountStep from "@pages/MergeAccount/Components/Step.vue";
  import MergeAccountStepDivider from "@pages/MergeAccount/Components/StepDivider.vue";
  import MergeAccountSteps from "@pages/MergeAccount/Components/Steps.vue";
  import {MedInfoButton} from "@pharma4u/medicheck-components";

  defineOptions({name: "MergeAccountShowStepsIntro"});
</script>

<template>
  <MergeAccountSteps>
    <MergeAccountStep :current-account="$page.props.client.id === $page.props.source.id">
      <MergeAccountIconGroup>
        <ComponentIcon v-if="$page.props.source.third_party_type === 'p4u'" class="shrink-0" custom="laptop" />
        <ComponentIcon v-else class="shrink-0" custom="front-desk" />
      </MergeAccountIconGroup>
      <div class="mt-6">
        <dl class="grid grid-cols-12 gap-2 text-base md:text-sm lg:text-base">
          <dt class="col-span-5 font-semibold">Fallarchiv</dt>
          <dd class="col-span-7">
            <span v-if="$page.props.source.third_party_type === 'p4u'">pharma4u</span>
            <span v-else>Warenwirtschaft</span>
            <span class="hidden md:block">
              <br />
            </span>
          </dd>

          <dt class="col-span-5 font-semibold">Patienten</dt>
          <dd class="col-span-7">{{ $page.props.source.patient_count }}</dd>

          <dt class="col-span-5 font-semibold">Analysen</dt>
          <dd class="col-span-7">{{ $page.props.source.record_count }}</dd>

          <dt class="col-span-5 font-semibold">Lizenz</dt>
          <dd class="col-span-7">
            {{ $page.props.source.license.product_name }} {{ $page.props.source.license.version_name }}
          </dd>
        </dl>

        <ComponentAlert v-if="$page.props.source.has_shared_records" type="warning" class="mt-4 text-sm">
          <strong class="font-semibold">Geteilte Analysen,</strong>
          die Sie an Dritte übertragen haben, werden beim Empfänger
          <strong class="font-semibold">widerrufen.</strong>
          Sie können diese nach der Verknüpfung einfach erneut teilen.
        </ComponentAlert>

        <ComponentAlert v-if="$page.props.source.has_pending_patientimport_transfers" type="error" class="mt-4 text-sm">
          Noch
          <strong class="font-semibold">nicht importierte Patientenfragebögen</strong>
          werden
          <strong class="font-semibold">verworfen.</strong>
          Sie sollten daher vor der Accountverknüpfung
          <component
            :is="$page.props.client.id === $page.props.source.id ? 'a' : 'span'"
            :href="$route('patientinput-client.index') + '?flags[]=status&flags[]=status:default'"
            target="_blank"
          >
            importiert
          </component>
          werden.
        </ComponentAlert>
      </div>
    </MergeAccountStep>

    <MergeAccountStepDivider>
      <ComponentIcon class="text-4xl">add</ComponentIcon>
    </MergeAccountStepDivider>

    <MergeAccountStep :current-account="$page.props.client.id === $page.props.target.id">
      <MergeAccountIconGroup>
        <ComponentIcon v-if="$page.props.target.third_party_type === 'p4u'" class="shrink-0" custom="laptop" />
        <ComponentIcon v-else class="shrink-0" custom="front-desk" />
      </MergeAccountIconGroup>

      <div class="mt-6">
        <dl class="grid grid-cols-12 gap-2 text-base md:text-sm lg:text-base">
          <dt class="col-span-5 font-semibold">Fallarchiv</dt>
          <dd class="col-span-7">
            <span v-if="$page.props.target.third_party_type === 'p4u'">pharma4u</span>
            <span v-else>Warenwirtschaft</span>
            <span class="hidden md:block">
              <br />
            </span>
          </dd>

          <dt class="col-span-5 font-semibold">Patienten</dt>
          <dd class="col-span-7">{{ $page.props.target.patient_count }}</dd>

          <dt class="col-span-5 font-semibold">Analysen</dt>
          <dd class="col-span-7">{{ $page.props.target.record_count }}</dd>

          <dt class="col-span-5 font-semibold">Lizenz</dt>
          <dd class="col-span-7">
            {{ $page.props.target.license.product_name }} {{ $page.props.target.license.version_name }}
          </dd>
        </dl>
        <div class="mt-4 text-sm font-semibold">
          <ComponentIcon class="inline-block">lock</ComponentIcon>
          Das Datenschutzpasswort dieses Accounts wird zukünftig für beide verwendet
        </div>
      </div>
    </MergeAccountStep>

    <MergeAccountStepDivider>
      <ComponentIcon class="rotate-90 text-4xl md:rotate-0">trending_flat</ComponentIcon>
    </MergeAccountStepDivider>

    <MergeAccountStep>
      <MergeAccountIconGroup>
        <ComponentIcon v-if="$page.props.source.third_party_type === 'p4u'" class="shrink-0" custom="laptop" />
        <ComponentIcon v-else class="shrink-0" custom="front-desk" />

        <ComponentIcon v-if="$page.props.target.third_party_type === 'p4u'" class="shrink-0" custom="laptop" />
        <ComponentIcon v-else class="shrink-0" custom="front-desk" />
      </MergeAccountIconGroup>
      <div class="mt-6">
        <dl class="grid grid-cols-12 gap-2 text-base md:text-sm lg:text-base">
          <dt class="col-span-5 font-semibold">Fallarchiv</dt>
          <dd class="col-span-7">
            <span v-if="$page.props.source.third_party_type === 'p4u'">pharma4u</span>
            <span v-else>Warenwirtschaft</span>
            +
            <br class="hidden md:block" />
            <span v-if="$page.props.target.third_party_type === 'p4u'">pharma4u</span>
            <span v-else>Warenwirtschaft</span>
          </dd>

          <dt class="col-span-5 font-semibold">Patienten</dt>
          <dd class="col-span-7">{{ $page.props.source.patient_count + $page.props.target.patient_count }}</dd>

          <dt class="col-span-5 font-semibold">Analysen</dt>
          <dd class="col-span-7">{{ $page.props.source.record_count + $page.props.target.record_count }}</dd>

          <template v-if="$page.props?.target?.license_recommendation">
            <dt class="col-span-5 font-semibold">empfohlene Lizenz</dt>
            <dd class="col-span-7">
              {{ $page.props.target.license_recommendation }}
              <MedInfoButton
                size="lg"
                side="top"
                aria-label="Lizenz Info"
                :data-test="getTestId('steps-intro-license-info-button')"
              >
                <p class="text-sm">
                  Als Nutzer des MediCheck Connect in Ihrer Warenwirtschaft erhalten Sie die Pro-Version zu einem
                  vergünstigten Preis. Wie das geht, erfahren Sie nach Abschluss der Verknüpfung.
                </p>
              </MedInfoButton>
            </dd>
          </template>
        </dl>
      </div>
    </MergeAccountStep>
  </MergeAccountSteps>
</template>
